import React, { useState } from 'react';
import { Users, Globe, Trash2, Edit2, Check, X, ExternalLink, Loader2 } from 'lucide-react';
import * as fbMonitorApi from '../../api/fbMonitor';

const SourceCard = ({ source, onUpdated, onDeleted }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(source.name || '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const handleToggleActive = async () => {
    setIsUpdating(true);
    setError(null);
    try {
      const updated = await fbMonitorApi.updateSource(source.id, {
        is_active: !source.is_active,
      });
      onUpdated(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) {
      setIsEditing(false);
      setEditName(source.name || '');
      return;
    }

    setIsUpdating(true);
    setError(null);
    try {
      const updated = await fbMonitorApi.updateSource(source.id, {
        name: editName.trim(),
      });
      onUpdated(updated);
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await fbMonitorApi.deleteSource(source.id);
      onDeleted(source.id);
    } catch (err) {
      setError(err.message);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditName(source.name || '');
  };

  const TypeIcon = source.type === 'group' ? Users : Globe;

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4">
      <div className="flex items-start gap-4">
        {/* Type Icon */}
        <div className={`p-2 rounded-lg ${source.is_active ? 'dark:bg-sky-500/10 bg-sky-50' : 'dark:bg-slate-800 bg-slate-100'}`}>
          <TypeIcon className={`w-5 h-5 ${source.is_active ? 'text-sky-500' : 'dark:text-slate-500 text-slate-400'}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name */}
          {isEditing ? (
            <div className="flex items-center gap-2 mb-1">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="flex-1 px-2 py-1 text-sm rounded border dark:bg-slate-800 bg-white dark:border-slate-700 border-slate-200 dark:text-white text-slate-900 focus:outline-none focus:border-sky-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') cancelEdit();
                }}
              />
              <button
                onClick={handleSaveName}
                disabled={isUpdating}
                className="p-1 text-green-500 hover:text-green-400"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={cancelEdit}
                disabled={isUpdating}
                className="p-1 text-slate-400 hover:text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium dark:text-white text-slate-900 truncate">
                {source.name || 'Unnamed Source'}
              </h3>
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-slate-400 hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Type Badge & URL */}
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              source.type === 'group'
                ? 'dark:bg-blue-500/10 dark:text-blue-400 bg-blue-50 text-blue-600'
                : 'dark:bg-purple-500/10 dark:text-purple-400 bg-purple-50 text-purple-600'
            }`}>
              {source.type === 'group' ? 'Group' : 'Page'}
            </span>
          </div>

          {/* URL */}
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-sky-400 truncate block flex items-center gap-1"
          >
            <span className="truncate">{source.url}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>

          {/* Error */}
          {error && (
            <p className="text-xs text-red-400 mt-2">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Active Toggle */}
          <button
            onClick={handleToggleActive}
            disabled={isUpdating}
            className={`relative w-10 h-6 rounded-full transition-colors ${
              source.is_active
                ? 'bg-sky-500'
                : 'dark:bg-slate-700 bg-slate-300'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                source.is_active ? 'left-5' : 'left-1'
              }`}
            />
          </button>

          {/* Delete */}
          {showDeleteConfirm ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="p-1.5 text-red-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="p-1.5 text-slate-400 hover:text-slate-300 hover:bg-slate-500/10 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceCard;
